/**
 * Draft-commit action bodies for the Target step (S4.T3a), extracted from
 * `dataImportStore.ts` to keep that file at state-shape + wiring (≤200 lines
 * per CLAUDE.md). `commitTarget` is the ONE atomic destination-change
 * transition (plan §2 Rev 10): native-type projection (+ schema-fetch/
 * auto-match/blocker-recompute once S6 lands) collapse into a SINGLE
 * `set()` once the `projectTypes` IPC call resolves, so the first Columns
 * paint after a destination change never observes a half-updated state.
 *
 * `applyOverrides` also lives here (moved from the store) since it now folds
 * in the projected native-type default alongside user column overrides —
 * both are "derive the effective column plan" logic or read from that
 * derivation, so keeping them together avoids splitting one concept across
 * two files. Only VALUES are imported back into `dataImportStore.ts` here —
 * any reference the other direction (e.g. `DataImportEntry`) is a type-only
 * import, which TypeScript erases at build time, so the two files never form
 * a runtime circular dependency.
 */
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { getEngineFromConnectionId } from '../../data-compare/utils/engineUtils';
import { presetNativeTypeToCanonical } from '../utils/presetNativeTypeToCanonical';
import { isStaleAnalysis } from './dataImportStore.staleness';
import { commitExistingTableTarget } from './dataImportStore.targetColumnsAction';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type {
  ImportAnalysis,
  ImportColumnPlan,
  ImportProjectTypesResponse,
  ImportTargetPlan,
} from '../../../../types/electron';
import type { DataImportColumnOverride, DataImportTabContent } from '../../core/types/workspaceTypes';
import type { DataImportEntry } from './dataImportStore';

/** The config-only fields both `defaultEntry` and `hydrate` seed from a tab's persisted content. */
export function contentFields(content?: DataImportTabContent) {
  return {
    filePath: content?.filePath,
    parse: content?.parse,
    target: content?.target,
    step: content?.step ?? 'source',
    columnOverrides: content?.columnOverrides ?? [],
    mappings: content?.mappings,
    rowFilter: content?.rowFilter,
  };
}

export function defaultEntry(content?: DataImportTabContent): DataImportEntry {
  return { ...contentFields(content), analysis: undefined, columns: [] };
}

export function ensureEntry(entries: Record<string, DataImportEntry>, tabId: string): DataImportEntry {
  return entries[tabId] ?? defaultEntry();
}

/**
 * Finding #7: the file-scoped fields that must be dropped whenever the source file changes — a new
 * pick via `setFilePath`, or "Import another file". `rowFilter.conditions` are `columnIndex`-keyed
 * and `mappings[].source.sourceIndex` are position-keyed, so against a DIFFERENT file they silently
 * point at unrelated columns (and an import-scope filter would import the wrong population);
 * `parse.sheetName` names a sheet that only exists in the OLD workbook. `columnOverrides` are
 * deliberately NOT cleared here — they're `sourceName`-keyed and re-apply only to a matching header
 * on the next file (the feature's point) — and the destination `target` is likewise kept.
 *
 * Returned as a spread-in patch so both the runtime store actions and the persisted-content mirror
 * (`useDataImportConfigActions`) clear the SAME set, or a surviving `content.rowFilter` would be
 * re-injected on the next `hydrate()` (the mirror-staleness bug class the config-actions doc warns
 * about). `columnOverrides` intentionally omitted so callers keep them untouched.
 */
export function clearFileScopedFields(
  entry: Pick<DataImportEntry, 'parse'>,
): Pick<DataImportEntry, 'rowFilter' | 'mappings' | 'parse'> {
  return {
    rowFilter: undefined,
    mappings: undefined,
    parse: entry.parse ? { ...entry.parse, sheetName: undefined } : entry.parse,
  };
}

/** Shown inline when a `projectTypes` IPC call rejects (T3a-review carry-forward #4) — the draft/
 * committed target are left untouched; only this transient field changes. */
export const PROJECT_TYPES_ERROR_MESSAGE = "Couldn't read the destination's column types. Try again.";

/** `projectTypes` wrapped so a rejection never becomes an unhandled promise rejection; callers get
 * `undefined` back and decide how to surface it (both `commitTargetAction` and
 * `reprojectNativeTypesAction` render `PROJECT_TYPES_ERROR_MESSAGE` on that signal). */
export async function fetchNativeTypes(
  engine: DatabaseEngineId,
  canonicals: ImportColumnPlan['canonical'][],
): Promise<string[] | undefined> {
  try {
    const response: ImportProjectTypesResponse | undefined = await window.electronAPI?.dataImport.projectTypes({
      engine,
      canonicals,
    });
    return response?.nativeTypes ?? [];
  } catch {
    return undefined;
  }
}

/**
 * Re-applies user column edits (and, when supplied, the last commit's
 * projected engine-native defaults) on top of a fresh analysis column list,
 * keyed by `sourceName`. Order matters (plan-mandated): the projected default
 * is applied FIRST, then `overrides` on top — so a user's free-text
 * `nativeType` (which also sets `nativeTypeIsCustom`) always wins over the
 * projected default, while non-custom columns pick up the fresh projection.
 *
 * S5 fix: a preset-matching override also re-derives `canonical` (via
 * `presetNativeTypeToCanonical` — see its header for the bug this closes) —
 * `engine` unresolved (pre-Target-commit) or a free-text override both leave
 * `canonical` untouched, matching prior behavior.
 */
export function applyOverrides(
  columns: ImportColumnPlan[],
  overrides: DataImportColumnOverride[],
  projectedNativeTypes?: Record<string, string>,
  engine?: DatabaseEngineId,
): ImportColumnPlan[] {
  if (overrides.length === 0 && !projectedNativeTypes) return columns;
  const bySourceName = overrides.length ? new Map(overrides.map((o) => [o.sourceName, o])) : undefined;

  return columns.map((col) => {
    const projected = projectedNativeTypes?.[col.sourceName];
    const override = bySourceName?.get(col.sourceName);
    if (!override && projected === undefined) return col;

    const overriddenCanonical =
      override?.nativeType !== undefined ? presetNativeTypeToCanonical(engine, override.nativeType) : undefined;

    return {
      ...col,
      ...(projected !== undefined ? { nativeType: projected } : {}),
      ...(override?.include !== undefined ? { include: override.include } : {}),
      ...(override?.name !== undefined ? { name: override.name } : {}),
      ...(override?.nativeType !== undefined
        ? { nativeType: override.nativeType, nativeTypeIsCustom: true }
        : {}),
      ...(overriddenCanonical ? { canonical: overriddenCanonical } : {}),
      ...(override?.dateFormat !== undefined ? { dateFormat: override.dateFormat } : {}),
      ...(override?.nullable !== undefined ? { nullable: override.nullable } : {}),
      ...(override?.inPrimaryKey !== undefined ? { inPrimaryKey: override.inPrimaryKey } : {}),
      ...(override ? { userEdited: true } : {}),
    };
  });
}

/** The entry's committed target's engine, or `undefined` before Target is committed. */
export function resolveEntryEngine(entry: Pick<DataImportEntry, 'target'>): DatabaseEngineId | undefined {
  const { connections, activeConnections } = useConnectionStore.getState();
  return getEngineFromConnectionId(entry.target?.connectionId, connections, activeConnections);
}

/** Stale-analysis guard shared by `applyAnalysis`/`applyPartial` (S4.T1 acceptance, Finding #7
 * generation-aware — see `dataImportStore.staleness.ts`); re-derives `columns` with the entry's
 * existing `projectedNativeTypes` so a mid-scan refinement never regresses a column back to its
 * raw canonical (pre-projection) native type. */
export function ingestAnalysis(entry: DataImportEntry, analysis: ImportAnalysis): DataImportEntry {
  if (entry.analysis && isStaleAnalysis(analysis, entry.analysis)) return entry;
  return {
    ...entry,
    analysis,
    columns: applyOverrides(analysis.columns, entry.columnOverrides, entry.projectedNativeTypes, resolveEntryEngine(entry)),
  };
}

/** Minimal completeness gate — just enough to resolve an engine and call `projectTypes`; the full field-by-field gate (schema-required, table-name) lives in `utils/targetStepGate.ts` and drives the footer CTA, so a real UI never invokes this on an incomplete draft. */
function canResolveEngine(draft: Partial<ImportTargetPlan> | undefined): draft is Partial<ImportTargetPlan> & { connectionId: string } {
  return !!draft?.connectionId;
}

/**
 * `commitTarget`'s async body. `entry` is a snapshot taken by the caller
 * before the IPC round-trip; `applyResult` is invoked exactly once, with the
 * FRESHEST entry re-read from the store at that moment, so any edits that
 * landed while the request was in flight (e.g. a column include toggle) are
 * not clobbered — only `target`/`draftTarget`/`projectedNativeTypes`/
 * `columns`/`rematch` are overwritten, and `columns` is rebuilt from the
 * freshest `analysis.columns` + `columnOverrides`.
 */
export async function commitTargetAction(
  entry: DataImportEntry,
  applyResult: (build: (current: DataImportEntry) => DataImportEntry) => void,
): Promise<void> {
  const draft = entry.draftTarget ?? entry.target;
  if (!canResolveEngine(draft)) return;

  // Existing-table mode has its own fetch (target-column facts, not projected
  // native types) + auto-match/blocker recompute — kept in a sibling module so
  // this file doesn't grow past its pre-existing over-cap size (S6.T2c-1).
  if ((draft.mode ?? 'new-table') === 'existing-table') {
    return commitExistingTableTarget(entry, draft, applyResult);
  }

  const { connections, activeConnections } = useConnectionStore.getState();
  const engine: DatabaseEngineId =
    getEngineFromConnectionId(draft.connectionId, connections, activeConnections) ?? 'mock';

  // Captured pre-await so the response array (order-preserved) can be zipped
  // back to `sourceName` unambiguously, regardless of any concurrent edit.
  const columnsAtRequestTime = entry.columns;
  const canonicals = columnsAtRequestTime.map((c) => c.canonical);

  const nativeTypes = await fetchNativeTypes(engine, canonicals);
  if (!nativeTypes) {
    // Draft/target/columns/rematch are left exactly as they were — a failed
    // commit is a no-op on everything except this transient error line.
    applyResult((current) => ({ ...current, projectTypesError: PROJECT_TYPES_ERROR_MESSAGE }));
    return;
  }

  applyResult((current) => {
    const projectedNativeTypes: Record<string, string> = {};
    columnsAtRequestTime.forEach((col, i) => {
      const native = nativeTypes[i];
      if (native !== undefined) projectedNativeTypes[col.sourceName] = native;
    });

    const columns = applyOverrides(
      current.analysis?.columns ?? current.columns,
      current.columnOverrides,
      projectedNativeTypes,
      engine,
    );
    const includedCount = columns.filter((c) => c.include).length;

    return {
      ...current,
      // S5 real-DB fix: `DestinationBar`'s `draft?.mode ?? 'new-table'` is a DISPLAY-only
      // fallback, never written back into the draft — a destination filled out without
      // touching the (visually already-selected) mode toggle committed `mode: undefined`,
      // so `emitImportCreateTable`'s `mode === 'new-table'` gate silently skipped CREATE
      // TABLE and every run failed at the first INSERT with a real "no such table" driver
      // error (the mock session doesn't enforce table existence, so no mock e2e caught this).
      target: { ...draft, mode: draft.mode ?? 'new-table' } as ImportTargetPlan,
      draftTarget: undefined,
      projectedNativeTypes,
      columns,
      projectTypesError: null,
      // New-table: every included column is its own insert target, so
      // matched === total === includedCount and there are no blockers. Only set
      // on a RE-commit (a prior target exists) — the toast announces that an
      // edit re-applied the user's column work; on the first commit nothing was
      // re-matched and it only added noise on arrival at Columns.
      rematch: current.target ? { matched: includedCount, total: includedCount, newBlockers: 0 } : null,
      // Mode flip existing-table → new-table: clear the target-derived facts a
      // prior existing-table commit left behind (symmetry with the existing-table
      // branch clearing projectedNativeTypes/projectTypesError) so no stale
      // targetColumns/mappings survive under a new-table target.
      targetColumns: undefined,
      existingRowCount: undefined,
      targetColumnsError: undefined,
      mappings: undefined,
    };
  });
}
